/**
 * Payer enrollment ID fields shared by the member card and the provider
 * information page.
 */

import { getStateOptions } from '@shared/constants/states';

// Mirrors the backend write bounds in jotpsych.models.provider_enrollment_ids.
export const MEDICARE_PTAN_MAX_LENGTH = 20;
export const MEDICAID_PROVIDER_ID_MAX_LENGTH = 30;

// Medicaid runs in the territories too; the backend accepts these codes.
const US_TERRITORY_OPTIONS = [
  { value: 'AS', name: 'American Samoa' },
  { value: 'GU', name: 'Guam' },
  { value: 'MP', name: 'Northern Mariana Islands' },
  { value: 'PR', name: 'Puerto Rico' },
  { value: 'VI', name: 'U.S. Virgin Islands' },
];

/** Medicaid state picker options: states and DC first, then territories. */
export const MEDICAID_STATE_OPTIONS: ReadonlyArray<{ value: string; name: string }> = [
  ...getStateOptions().map(({ value, name }) => ({ value, name })),
  ...US_TERRITORY_OPTIONS,
];

/** A stored Medicaid state the picker doesn't list (shown as a saved value). */
export function isUnlistedMedicaidState(value: string): boolean {
  return value.length > 0 && !MEDICAID_STATE_OPTIONS.some(option => option.value === value);
}

/**
 * Length error for an enrollment ID, or undefined when it fits. The input is
 * not hard-capped: a pasted ID must not be silently cut into a wrong one.
 */
export function enrollmentIdLengthError(
  value: string | null | undefined,
  maxLength: number,
): string | undefined {
  return (value ?? '').trim().length > maxLength ? `At most ${maxLength} characters` : undefined;
}

type EnrollmentFields = {
  medicare_ptan?: string | null;
  medicaid_provider_id?: string | null;
  medicaid_state?: string | null;
};

/**
 * True when an enrollment field differs from its saved value and fails the
 * client check (too long, or a Medicaid state that isn't a listed code).
 * Autosave skips the save while this holds. A saved value left unchanged
 * never blocks, matching the backend, which only re-checks changed values.
 */
export function hasInvalidEnrollmentChange(
  next: EnrollmentFields,
  saved: EnrollmentFields,
): boolean {
  const changed = (key: keyof EnrollmentFields): string | undefined => {
    const value = next[key] ?? '';
    return value !== (saved[key] ?? '') ? value : undefined;
  };
  const ptan = changed('medicare_ptan');
  const medicaidId = changed('medicaid_provider_id');
  const medicaidState = changed('medicaid_state');
  return (
    (ptan !== undefined && Boolean(enrollmentIdLengthError(ptan, MEDICARE_PTAN_MAX_LENGTH))) ||
    (medicaidId !== undefined &&
      Boolean(enrollmentIdLengthError(medicaidId, MEDICAID_PROVIDER_ID_MAX_LENGTH))) ||
    (medicaidState !== undefined && isUnlistedMedicaidState(medicaidState))
  );
}

/**
 * Thrown by an autosave handler instead of skipping silently, so the autosave
 * status shows not-saved rather than "Saved" while an enrollment field is
 * invalid. Nothing is sent; the next valid change saves every pending edit.
 */
export class InvalidEnrollmentChangeError extends Error {
  constructor() {
    super('Fix the highlighted field to save your changes.');
    this.name = 'InvalidEnrollmentChangeError';
  }
}
